import { useState, useEffect, useCallback } from "react";
import { useStore } from "@/stores/useStore";
import { metrics } from "@/repositories/metrics";
import { ContainerTimeConfig, MetricsConfigs } from "@/types/v2/Metrics";

export const useMetricsConfigs = () => {
  const { shopId } = useStore();
  const [configs, setConfigs] = useState<MetricsConfigs | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load configurations
  const loadConfigs = useCallback(async () => {
    if (!shopId) return;

    try {
      setLoading(true);
      setError(null);
      const loadedConfigs = await metrics.getConfigs(shopId);
      setConfigs(loadedConfigs || { containerTimeConfigs: [] });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to load configurations"
      );
    } finally {
      setLoading(false);
    }
  }, [shopId]);

  // Save a new container time configuration
  const saveContainerTimeConfig = useCallback(
    async (
      config: Omit<
        ContainerTimeConfig,
        "id" | "createdAt" | "updatedAt" | "createdBy"
      >
    ) => {
      if (!shopId) throw new Error("No shop ID available");

      try {
        const newConfig = await metrics.addContainerTimeConfig(shopId, config);
        await loadConfigs(); // Reload configs to get the updated list
        return newConfig;
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to save configuration"
        );
        throw err;
      }
    },
    [shopId, loadConfigs]
  );

  // Update an existing container time configuration
  const updateContainerTimeConfig = useCallback(
    async (
      configId: string,
      updates: Partial<
        Omit<ContainerTimeConfig, "id" | "createdAt" | "createdBy">
      >
    ) => {
      if (!shopId) throw new Error("No shop ID available");

      try {
        const updatedConfig = await metrics.updateContainerTimeConfig(
          shopId,
          configId,
          updates
        );
        await loadConfigs(); // Reload configs to get the updated list
        return updatedConfig;
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to update configuration"
        );
        throw err;
      }
    },
    [shopId, loadConfigs]
  );

  // Delete a container time configuration
  const deleteContainerTimeConfig = useCallback(
    async (configId: string) => {
      if (!shopId) throw new Error("No shop ID available");

      try {
        await metrics.deleteContainerTimeConfig(shopId, configId);
        await loadConfigs(); // Reload configs to get the updated list
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to delete configuration"
        );
        throw err;
      }
    },
    [shopId, loadConfigs]
  );

  // Load configurations on mount
  useEffect(() => {
    loadConfigs();
  }, [loadConfigs]);

  return {
    configs,
    loading,
    error,
    loadConfigs,
    saveContainerTimeConfig,
    updateContainerTimeConfig,
    deleteContainerTimeConfig,
  };
};
